import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Text,
  useToast,
  Divider,
  Box,
  Icon,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiUser, FiMapPin, FiTag, FiCheck } from 'react-icons/fi';
import UserPicker, { User } from '@/components/common/UserPicker';
import LocationPicker, { LocationData } from '@/components/common/LocationPicker';
import apiClient, { Device } from '@/services/letsparkyClient';
import { useAuthStore } from '@/stores/authStore';

interface InitializeDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: Device | null;
  onSuccess?: () => void;
}

interface FormData {
  selectedUser: User | null;
  nickName: string;
  location: LocationData | null;
}

const InitializeDeviceModal: React.FC<InitializeDeviceModalProps> = ({
  isOpen,
  onClose,
  device,
  onSuccess,
}) => {
  const toast = useToast();
  const { token } = useAuthStore();
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState<FormData>({
    selectedUser: null,
    nickName: '',
    location: null,
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("blue.50", "blue.900");

  useEffect(() => {
    if (isOpen) {
      fetchAvailableUsers();
      // Reset form when modal opens
      setFormData({
        selectedUser: null,
        nickName: device?.nick_name || '',
        location: null,
      });
      setErrors({});
    }
  }, [isOpen, device]);

  const fetchAvailableUsers = async () => {
    setIsLoadingUsers(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      
      const response = await apiClient.users.list({ limit: 2000, sort: 'updated_at', order: 'desc' as const });
      if (response && response.data && Array.isArray(response.data.data)) {
        setAvailableUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error loading users',
        description: 'Could not load available users for assignment',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.selectedUser) {
      newErrors.user = 'Please select a user';
    }

    if (!formData.nickName.trim()) {
      newErrors.nickName = 'Device name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !device || !formData.selectedUser) return;

    setIsSaving(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      
      await apiClient.devices.addOwner({
        serial_no: device.serial_no,
        user_id: formData.selectedUser.id,
        nick_name: formData.nickName?.trim(),
        geo_lat: formData.location?.latitude,
        geo_lng: formData.location?.longitude,
        location: formData.location?.address,
        qr_data: device.qr_data,
        photo: device.photo,
      });
      
      toast({
        title: 'Device initialized successfully',
        description: `${formData.selectedUser.name || formData.selectedUser.email || formData.selectedUser.phone_number} has been assigned as the owner of ${formData.nickName}`,
        status: 'success',
        duration: 4000,
      });
      
      onSuccess?.();
      onClose();
    } catch (error) {
      toast({
        title: 'Failed to initialize device',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUserChange = (user: User | null) => {
    setFormData(prev => ({ ...prev, selectedUser: user }));
    if (errors.user) {
      setErrors(prev => ({ ...prev, user: '' }));
    }
  };

  const handleLocationChange = (location: LocationData) => {
    setFormData(prev => ({ ...prev, location }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent>
        <ModalHeader bg={headerBg} borderTopRadius="md">
          <HStack spacing={3}>
            <Icon as={FiUser} boxSize={5} color="blue.500" />
            <VStack align="start" spacing={0}>
              <Text>Initialize Device</Text>
              <Text fontSize="sm" fontWeight="normal" color="gray.600">
                Set up and assign owner to {device?.nick_name || device?.serial_no}
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody p={6}>
          <VStack spacing={6} align="stretch">
            {/* Device Info Card */}
            <Box
              p={4}
              borderWidth="1px"
              borderRadius="md"
              borderColor={borderColor}
              bg={cardBg}
            >
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <Text fontWeight="medium" color="gray.700">
                    Device Information
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Serial: {device?.serial_no}
                  </Text>
                </VStack>
                <Badge colorScheme="blue" variant="subtle">
                  Unowned
                </Badge>
              </HStack>
            </Box>

            {/* User Selection */}
            <Box>
              <FormLabel mb={3}>
                <HStack>
                  <Icon as={FiUser} boxSize={4} />
                  <Text>Select Device Owner</Text>
                </HStack>
              </FormLabel>
              <UserPicker
                users={availableUsers}
                value={formData.selectedUser}
                onChange={handleUserChange}
                placeholder="Search and select a user..."
                isLoading={isLoadingUsers}
                error={errors.user}
                isRequired
              />
            </Box>

            {/* Device Nickname */}
            <FormControl isInvalid={!!errors.nickName} isRequired>
              <FormLabel>
                <HStack>
                  <Icon as={FiTag} boxSize={4} />
                  <Text>Device Name</Text>
                </HStack>
              </FormLabel>
              <Input
                placeholder="Enter a friendly name for the device"
                value={formData.nickName}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, nickName: e.target.value }));
                  if (errors.nickName) {
                    setErrors(prev => ({ ...prev, nickName: '' }));
                  }
                }}
              />
              <FormErrorMessage>{errors.nickName}</FormErrorMessage>
            </FormControl>

            <Divider />

            {/* Location Selection */}
            <FormControl>
              <FormLabel>
                <HStack>
                  <Icon as={FiMapPin} boxSize={4} />
                  <Text>Device Location</Text>
                  <Badge variant="outline" colorScheme="gray">Optional</Badge>
                </HStack>
              </FormLabel>
              <LocationPicker
                value={formData.location || undefined}
                onChange={handleLocationChange}
                placeholder="Search for device location or click on map"
                height="250px"
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={isSaving}
              loadingText="Initializing Device..."
              leftIcon={<Icon as={FiCheck} />}
              isDisabled={!formData.selectedUser || !formData.nickName.trim()}
            >
              Initialize Device
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default InitializeDeviceModal;